import { Button, Checkbox, Flex, Form, Input, Typography, notification } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useStyles from "./styles";
import { useTheme } from "antd-style";
import { useLink, useLogin } from "@refinedev/core";
import { usePageTitle } from "@/hooks/usePageTitle";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { VitriaIcon } from "@/components/VitriaIcon";
import { useSearchParams } from "react-router-dom";

export const LoginPage = ({ showRememberMe, showForgotPasswordLink }) => {
  const { t } = useTranslation();
  const token = useTheme();
  const Link = useLink();
  const [searchParams] = useSearchParams();
  const tenantName = searchParams.get("tenant");
  const user = searchParams.get("user");
  const [showCustomDomain, setShowCustomDomain] = useState(false);
  const { mutateAsync: loginAsync, isLoading } = useLogin();
  const [mfa, setMfa] = useState(null);
  const [mfaFormInstance] = Form.useForm();

  usePageTitle(t("auth.loginTitle", "VIA AIOps"));

  const { styles } = useStyles();
  const MFA_MAP = {
    "SOFTWARE_TOKEN_MFA": "authenticator app",
    "EMAIL_OTP": "email box"
  }

  const doJumpToCustomDomain = async ({ customDomain }) => {
    window.location.href = `/api/v1/provision/jump/${customDomain}`;
  };

  const doLogin = async ({ tenant, username, password, remember }) => {
    const resp = await loginAsync({ tenant, username, password, remember });
    if (resp.success && resp.challenge) {
      if("MFA_SETUP" === resp.challenge) {
        notification.error({
          "description": t("pages.login.errors.setupMFA", "Please bind the authentication device first or let admin to change another MFA authenticaion type!"),
          duration: 8
        })
      } else
        setMfa(resp);
    }
  };

  const doMfaLogin = async ({ code, remember }) => {
    const resp = await loginAsync({ code, remember, challenge: mfa.challenge });
    if (resp.success) {
      setMfa(null);
    } else {
      if (resp.error_code === 1 && resp.value.indexOf("expired") > -1) {
        mfaFormInstance.setFields([
          {
            name: "code",
            errors: [t("pages.login.errors.codeExpired", "code expired")],
          },
        ]);
      } else {
        mfaFormInstance.setFields([
          {
            name: "code",
            errors: [t("pages.login.errors.invalidCode", "Invalid code")],
          },
        ]);
      }
    }
  };

  const adminLoginForm = (
    <Form layout="horizontal" size="middle" requiredMark={false} onFinish={doLogin} style={{ marginTop: 20 }}>
      <Form.Item name="tenant" disabled initialValue={tenantName || ""}>
        <Input prefix={<VitriaIcon type="vicon-tenant" />} placeholder={t("pages.login.fields.tenant", "Tenant")} disabled={tenantName}/>
      </Form.Item>
      <Form.Item
        name="username"
        rules={[
          {
            required: true,
            message: t("pages.login.errors.requiredUsername", "Username is required"),
          },
        ]}
      >
        <Input prefix={<UserOutlined />} placeholder={t("pages.login.fields.username", "Username")} />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          {
            required: true,
            message: t("pages.login.errors.requiredPassword", "Password is required"),
          },
        ]}
      >
        <Input prefix={<LockOutlined />} type="password" autoComplete="current-password" placeholder="●●●●●●●●" />
      </Form.Item>
      {(showRememberMe || showForgotPasswordLink) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          {showRememberMe && (
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox
                style={{
                  fontSize: "12px",
                }}
              >
                {t("pages.login.buttons.rememberMe", "Remember me")}
              </Checkbox>
            </Form.Item>
          )}
          {showForgotPasswordLink && (
            <Link
              style={{
                color: token.colorPrimaryTextHover,
                fontSize: "12px",
                marginLeft: "auto",
              }}
              to="/forgot-password"
            >
              {t("pages.login.buttons.forgotPassword", "Forgot password?")}
            </Link>
          )}
        </div>
      )}
      <Form.Item style={{ marginBottom: 12 }}>
        <Button type="primary" size="large" htmlType="submit" loading={isLoading} block>
          {t("pages.login.signin", "Sign in")}
        </Button>
      </Form.Item>
      <div
        style={{
          marginTop: 8,
          textAlign: "center",
        }}
      >
        <Typography.Text style={{ fontSize: 12 }}>
          <Link
            style={{
              fontWeight: "bold",
              color: token.colorPrimaryTextHover,
            }}
            onClick={() => setShowCustomDomain(true)}
          >
            {t("pages.login.buttons.customDomainLink", "Use Custom Domain")}
          </Link>
        </Typography.Text>
      </div>
      <hr />
      <div
        style={{
          marginTop: 8,
          textAlign: "center",
        }}
      >
        <Typography.Text style={{ fontSize: 12 }}>
          {t("pages.login.buttons.noAccount", "Don’t have an account?")}{" "}
          <Link
            to="/register"
            style={{
              fontWeight: "bold",
              color: token.colorPrimaryTextHover,
            }}
          >
            {t("pages.login.signup", "Sign up")}
          </Link>
        </Typography.Text>
      </div>
    </Form>
  );

  const mfaForm = (
    <Form
      form={mfaFormInstance}
      layout="vertical"
      requiredMark={false}
      onFinish={(values) => doMfaLogin({ ...values, remember: true })}
      style={{ marginTop: 24 }}
    >
      <Form.Item>
        <Typography.Text>{t("pages.login.mfaDescription.start", "Enter the code sent to your ")}</Typography.Text>
        <Typography.Text strong>{MFA_MAP[mfa?.challenge]}</Typography.Text>
      </Form.Item>
      <Form.Item
        name="code"
        label={t("pages.login.fields.code", "Code")}
        rules={[
          {
            required: true,
            message: t("pages.login.errors.requiredCode", "Code is required"),
          },
        ]}
      >
        <Input placeholder={t("pages.login.fields.code", "Code")} />
      </Form.Item>
      <Form.Item style={{ marginBottom: 12 }}>
        <Flex gap="middle" justify="space-between">
          <Button size="large" loading={isLoading} block onClick={() => setMfa(null)}>
            {t("pages.login.buttons.back", "Back")}
          </Button>
          <Button type="primary" size="large" htmlType="submit" loading={isLoading} block>
            {t("pages.login.buttons.continue", "Continue")}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  );

  const customDomainForm = (
    <Form
      layout="vertical"
      requiredMark={false}
      onFinish={(values) => doJumpToCustomDomain(values)}
      style={{ marginTop: 24 }}
    >
      <Form.Item>
        <Typography.Text>
          {t("pages.login.customDomainDescription", "Enter your custom domain to sign in")}
        </Typography.Text>
      </Form.Item>
      <Form.Item
        name="customDomain"
        label={t("pages.login.fields.customDomain", "Custom Domain")}
        rules={[
          {
            required: true,
            message: t("pages.login.errors.requiredCustomDomain", "Custom domain is required"),
          },
        ]}
      >
        <Input placeholder={t("pages.login.fields.customDomain", "Custom Domain")} />
      </Form.Item>
      <Form.Item style={{ marginBottom: 12 }}>
        <Flex gap="middle" justify="space-between">
          <Button size="large" loading={isLoading} block onClick={() => setShowCustomDomain(false)}>
            {t("pages.login.buttons.back", "Back")}
          </Button>
          <Button type="primary" size="large" htmlType="submit" loading={isLoading} block>
            {t("pages.login.buttons.continue", "Continue")}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  );

  return (
    <div className={styles.loginContainer}>
      <div className={styles.formContainer}>
        <div className={styles.pageTitle}>
          {/* <AIOpsLogo /> */}
          <span style={{ color: token.colorPrimary, fontSize: "28px", fontWeight: 700 }}>
            {t("auth.loginFormTitle", "VIA AIOps")}
          </span>
        </div>
        {!showCustomDomain && !mfa && adminLoginForm}
        {showCustomDomain && !mfa && customDomainForm}
        {mfa && mfaForm}
      </div>
    </div>
  );
};
